// Client-side payment funnel instrumentation. Stripe can only tell us what
// happened from checkout-session creation onward; these events cover the
// steps before that (plan picked, account created, iframe mounted) so we can
// see exactly where someone leaves the payment flow.
const SESSION_KEY = "mnry_pay_sid";

export type PaymentFunnelEvent =
  | "upgrade_nudge_shown"
  // The moment an offer is actually put in front of someone: the expired-trial
  // paywall rendering, and a plan card being clicked on it. Without these the
  // funnel cannot tell "saw the offer and declined" from "never saw one".
  | "paywall_shown"
  | "paywall_plan_clicked"
  | "plan_selected"
  | "account_form_shown"
  | "account_created"
  | "account_error"
  | "confirm_required"
  | "checkout_mounted"
  | "checkout_session_error"
  | "checkout_dismissed"
  | "returned_success"
  | "returned_no_session"
  | "subscription_active"
  // Booking-call funnel: viewing the /book page and clicking through to
  // Calendly. Not part of the ordered payment steps.
  | "booking_view"
  | "booking_click";

/** Ordered funnel steps used by the admin view. */
export const PAYMENT_FUNNEL_STEPS: Array<{ step: number; label: string; events: PaymentFunnelEvent[] }> = [
  // Step 0 so the existing numbered steps keep the meaning they already have
  // in stored rows.
  { step: 0, label: "Offer seen", events: ["paywall_shown", "paywall_plan_clicked"] },
  { step: 1, label: "Plan selected", events: ["plan_selected"] },
  { step: 2, label: "Account created", events: ["account_created"] },
  { step: 3, label: "Checkout opened", events: ["checkout_mounted"] },
  { step: 4, label: "Returned from Stripe", events: ["returned_success"] },
  { step: 5, label: "Subscription active", events: ["subscription_active"] },
];

const STEP_BY_EVENT: Record<string, { step: number; label: string }> = (() => {
  const map: Record<string, { step: number; label: string }> = {};
  for (const s of PAYMENT_FUNNEL_STEPS) {
    for (const e of s.events) map[e] = { step: s.step, label: s.label };
  }
  return map;
})();

export function paymentFunnelStep(event: string): { step: number; label: string } | null {
  return STEP_BY_EVENT[event] ?? null;
}

export function paymentSessionId(): string {
  if (typeof window === "undefined") return "server";
  try {
    let id = window.sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `pay_${Math.random().toString(36).slice(2)}${Date.now().toString(36)}`;
      window.sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return `pay_anon_${Date.now().toString(36)}`;
  }
}

export function trackPayment(
  event: PaymentFunnelEvent,
  payload: {
    plan?: string | null;
    priceId?: string | null;
    email?: string | null;
    userId?: string | null;
    environment?: string | null;
    detail?: string | null;
    meta?: Record<string, unknown>;
  } = {},
): void {
  if (typeof window === "undefined") return;
  const body = JSON.stringify({
    sessionId: paymentSessionId(),
    event,
    plan: payload.plan ?? null,
    priceId: payload.priceId ?? null,
    email: payload.email ?? null,
    userId: payload.userId ?? null,
    environment: payload.environment ?? null,
    detail: payload.detail ? payload.detail.slice(0, 500) : null,
    meta: payload.meta ?? {},
  });
  try {
    if (navigator.sendBeacon) {
      navigator.sendBeacon("/api/public/payments/event", new Blob([body], { type: "application/json" }));
      return;
    }
  } catch {
    /* fall through to fetch */
  }
  void fetch("/api/public/payments/event", {
    method: "POST",
    headers: { "content-type": "application/json" },
    body,
    keepalive: true,
  }).catch(() => {});
}
